import React, { ReactNode, useEffect, useState } from 'react';
import ReactDOM from 'react-dom';

import Button from '../Button';
import { useDelay } from '@/hooks';
import { twMerge } from '@utilities';

export type DrawerProps = {
  /** Controls whether the drawer is open or closed */
  isOpen: boolean;
  /** Callback fired when the drawer should close */
  onClose: () => void;
  /** Position of the drawer on the screen */
  position?: 'left' | 'right';
  /** Header content */
  header?: ReactNode;
  /** Main content of the drawer */
  children: ReactNode;
  /** Footer content */
  footer?: ReactNode;
  /** Whether to close drawer when backdrop is clicked */
  closeOnBackdropClick?: boolean;
  /** Custom width (e.g., '400px', '50%', 'w-96') */
  width?: string;
  /** Additional CSS class names */
  className?: string;
};

/**
 * Drawer Component
 * A fully controlled, flexible drawer with backdrop, header, content, and footer sections.
 *
 * @example
 * const [isOpen, setIsOpen] = useState(false);
 *
 * <Drawer
 *   isOpen={isOpen}
 *   onClose={() => setIsOpen(false)}
 *   header={<h2>Drawer Title</h2>}
 *   footer={<button onClick={() => setIsOpen(false)}>Close</button>}
 * >
 *   <p>Drawer content goes here</p>
 * </Drawer>
 */
export const Drawer: React.FC<DrawerProps> = ({
  isOpen,
  onClose,
  position = 'right',
  header,
  children,
  footer,
  closeOnBackdropClick = true,
  width = '400px',
  className = '',
}) => {
  const [hasMounted, setHasMounted] = useState(false);
  const delayedOpen = useDelay<boolean>({
    delay: 50,
    value: isOpen,
    defaultValue: false,
  });

  useEffect(() => {
    setHasMounted(true);
  }, []);
  const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (closeOnBackdropClick && e.target === e.currentTarget) {
      onClose();
    }
  };

  if (!hasMounted) return null;

  const slideDirection =
    position === 'left'
      ? delayedOpen
        ? 'translate-x-0'
        : '-translate-x-full'
      : delayedOpen
        ? 'translate-x-0'
        : 'translate-x-full';

  const element = (
    <>
      <div
        data-name="drawer-component-backdrop"
        aria-hidden={!delayedOpen}
        className={twMerge(
          'fixed inset-0 z-40 bg-gray-500 transition-opacity duration-300 shadow-md',
          delayedOpen ? 'opacity-50' : 'pointer-events-none opacity-0'
        )}
        onClick={handleBackdropClick}
      />

      <div
        role="dialog"
        aria-modal="true"
        data-name="drawer-component-content"
        aria-hidden={!delayedOpen}
        className={twMerge(
          `fixed top-0 z-50 flex h-screen w-full flex-col bg-content shadow-lg transition-transform duration-300 ease-in-out`,
          position === 'left' ? 'left-0' : 'right-0',
          slideDirection,
          className
        )}
        style={{ maxWidth: width, width }}
      >
        {/* Header */}
        {header && (
          <div className="border-b border-active px-lg py-md flex flex-row items-center justify-between">
            <div className="truncate">{header}</div>
            <Button
              round
              width="big"
              icon="clear"
              variant="icon"
              onClick={onClose}
              className="ml-auto"
            />
          </div>
        )}

        {/* Content */}
        <div className="flex-1 flex overflow-y-auto px-lg py-md">
          {children}
        </div>

        {/* Footer */}
        {footer && (
          <div className="border-t border-border px-lg py-md">{footer}</div>
        )}
      </div>
    </>
  );

  return isOpen ? ReactDOM.createPortal(element, document.body) : null;
};

export default Drawer;
